import {Paper, SegmentedControl, Switch, Typography} from "@mantine/core";
import {LineChart} from "@mantine/charts";
import {useMemo, useState} from "react";
import {Row, Col} from "../../components/Flex/Flex";
import Icon from "../../components/Icon/Icon";
import IconMap from "../../components/Icon/IconMap";
import {clamp} from "../map/utils";
import {chartPalette} from "./chartUtils";
import {doors} from './doorUtils';

const skill = [0,1,2,3,4,5];

const calculateInstallChance = (door, skill) => {
    return clamp(100 - (35*door) + (15*skill), 0, 100);
}
const calculateHackStrength  = (crafting, co, multiplier) => {
    const exp1 = 1+((crafting+0.5*co) / 5);
    const exp2 = 1+((0.5*crafting+co) / 5);

    return (0.5 + 2*(Math.pow(crafting, exp1) + Math.pow(0.653*co, exp2))) * multiplier
}
const calculateDoorHack = (firewall, crafting, co, multiplier) => {
    const str = calculateHackStrength(crafting, co, multiplier);

    return (str / (str+firewall))*100;
}

// Chance (0-100%) of clearing one firewall for a given crafting/CO pair.
export const hackChance = (door, crafting, co) => calculateDoorHack(door.firewallStrength, crafting, co, 1);
// Average hours to break the whole lock at that chance.
export const hackTime = (door, crafting, co) => Math.ceil((door.firewalls * door.hoursPerFW) / (hackChance(door, crafting, co) / 100));
// Chance (0-100%) of losing the lockbreaker: (1 - p) / 5, where p is the success chance as a decimal.
export const critFail = (door, crafting, co) => (100 - hackChance(door, crafting, co)) / 5;


// Series for the two orientations: one line per skill level, or one line per door.
const skillSeries = skill.map((s, i) => ({name: String(s), color: chartPalette[i]}));
const doorSeries = doors.map((door, i) => ({name: door.name, color: chartPalette[i]}));

// One row per door, with a value for each skill level (x-axis = door).
const byDoor = (fn) => doors.map(door => {
    const row = {x: door.name};
    skill.forEach(s => { row[s] = fn(door.lvl, s); });
    return row;
});
// One row per skill level, with a value for each door (x-axis = skill).
const bySkill = (fn) => skill.map(s => {
    const row = {x: s};
    doors.forEach(door => { row[door.lvl] = fn(door.lvl, s); });
    return row;
});

// Both hack skills range 0-5, so the axis and the series share the same domain.
// Non-flipped: x-axis = Crafting, one line per Computer Operations level.
// Flipped:     x-axis = Computer Operations, one line per Crafting level.
const hackMatrix = (door, valueFn, flipped) => skill.map(a => {
    const row = {x: a};
    skill.forEach(b => {
        const crafting = flipped ? b : a;
        const co = flipped ? a : b;
        row[b] = valueFn(door, crafting, co);
    });
    return row;
});

const chartInstall = byDoor(calculateInstallChance);
const chartInstallFlipped = bySkill(calculateInstallChance);

const decimal = new Intl.NumberFormat(undefined, {maximumFractionDigits:2})
const integer = new Intl.NumberFormat(undefined, {maximumFractionDigits:0});

export default function Locks () {

    const [flipped, setFlipped] = useState(false);
    // The two hack sections depend on Crafting AND Computer Operations, so their
    // full skill grid is shown one door at a time.
    const [doorLvl, setDoorLvl] = useState(String(doors[0].lvl));
    const door = doors.find(d => String(d.lvl) === doorLvl) ?? doors[0];

    const chartHackChance = useMemo(() => hackMatrix(door, hackChance, flipped), [door, flipped]);
    const chartHackTime = useMemo(() => hackMatrix(door, hackTime, flipped), [door, flipped]);
    const chartHackCrit = useMemo(() => hackMatrix(door, critFail, flipped), [door, flipped]);

    const axisLabel = flipped ? 'Computer Operations' : 'Crafting';
    const lineLabel = flipped ? 'Crafting' : 'Computer Operations';


    return (
        <div className="w-full">
            <title>Door Locks and Slicing</title>
            <Row align="center">
                <Col><h1>Locks</h1></Col>
                <Col grow={1} />
                <Col><a className="link no-underline" href="https://www.swcombine.com/rules/?Locks_and_Keys" target="_blank" rel="noreferrer"><Icon icon={IconMap.SwcRules} /> Full rules</a></Col>
            </Row>
            <Paper mb={4}>
                <div className="float-right flex flex-row">
                    <label htmlFor="flipped" className="mr-1">Switch Graph Axis</label>
                    <Switch id="flipped" checked={flipped} onChange={e => setFlipped(e.currentTarget.checked)} />
                </div>
                <h2>Installation</h2>
                <h3>Chance of installing</h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th />
                                <th colSpan={skill.length + 1}>Crafting Skill</th>
                            </tr>
                            <tr>
                                <th>Door</th>
                                <th>Install time</th>
                                {skill.map(s => <th key={s}>{s}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {doors.map(door => (
                                <tr key={door.lvl}>
                                    <th>{door.name}</th>
                                    <td className="text-center">{door.lvl*4}H</td>
                                    {skill.map(s => <td key={s} className="text-right">{calculateInstallChance(door.lvl, s)}%</td>)}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={flipped ? chartInstallFlipped : chartInstall} series={flipped ? doorSeries : skillSeries} />
                    </Col>
                </Row>

            </Paper>
            <Paper className="mb-3">
                <Row align="center" mb={2}>
                    <Col><h2 className="mt-0 mb-0">Breaking</h2></Col>
                    <Col grow={1} />
                    <Col>
                        <SegmentedControl value={doorLvl} color={"forge"} onChange={setDoorLvl} data={doors.map(d => ({value: String(d.lvl), label: d.name}))} />
                    </Col>
                </Row>
                <p className="text-blue-200 mt-0">Breaking depends on both <b>Crafting</b> and <b>Computer Operations</b>. Rows are Crafting, columns are Computer Operations, for the selected door.</p>
                <h3>Chance of breaking lock <small>({door.name})</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => {
                                        let v = hackChance(door, crafting, co);
                                        let color;
                                        if(v<50) {
                                            color = 'red'
                                        } else if(v<80) {
                                            color = 'yellow'
                                        } else {
                                            color = 'green';
                                        }
                                        return <td key={co} className="text-right" title={`${decimal.format(v)}%`}><Typography c={color}>{integer.format(v)}%</Typography></td>;
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackChance} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="%" />
                        <p className="text-center"><small>Lines = {lineLabel} level</small></p>
                    </Col>
                </Row>
                <h3>Average time to break <small>({door.name}, Hours — base {door.firewalls * door.hoursPerFW}H)</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => <td key={co} className="text-right">{integer.format(hackTime(door, crafting, co))}H</td>)}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackTime} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="Hours" />
                        <p className="text-center"><small>Lines = {lineLabel} level</small></p>
                    </Col>
                </Row>
                <h3>Chance of losing lockbreaker <small>({door.name})</small></h3>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="50%">
                        <table className="w-full">
                            <thead>
                            <tr>
                                <th/>
                                <th colSpan={skill.length}>Computer Operations</th>
                            </tr>
                            <tr>
                                <th>Crafting</th>
                                {skill.map(co => <th key={co}>{co}</th>)}
                            </tr>
                            </thead>
                            <tbody>
                            {skill.map(crafting => (
                                <tr key={crafting}>
                                    <th>{crafting}</th>
                                    {skill.map(co => {
                                        let v = critFail(door, crafting, co);
                                        let color;
                                        if(v < 5) {
                                            color = 'green';
                                        } else if(v<15) {
                                            color = 'yellow';
                                        } else {
                                            color = 'red';
                                        }
                                        return <td key={co} className="text-right" title={`${decimal.format(v)}%`}><Typography c={color}>{decimal.format(v)}%</Typography></td>;
                                    })}
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </Col>
                    <Col basis="50%">
                        <LineChart h={300} dataKey="x" withLegend curveType="linear" data={chartHackCrit} series={skillSeries} xAxisLabel={axisLabel} yAxisLabel="%" />
                        <p className="text-center"><small>Lines = {lineLabel} level</small></p>
                    </Col>
                </Row>
            </Paper>
        </div>

    )

}
